/**
 * Course player progress bar.
 */
import React from "react";
import { Box, CircularProgress } from "@mui/material";
import { circularProgressClasses } from "@mui/material/CircularProgress";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { VText } from "form";
import { useAsterController } from "context";

/**
 * Displays weighted progress for the current course.
 */
const BoardProgressBar = () => {
  const { t } = useTranslation("common");

  const [controller] = useAsterController();
  const { structure, moduleInspects, viewedVerticals, loadedCourseDetail } = controller;

  const unitCount = useMemo(() => {
    if (loadedCourseDetail) {
      let counter = 0;

      if (structure.sections) {
        for (let section of structure.sections) {
          if (!section.sequences) continue;
          for (let sequence of section.sequences) {
            counter += sequence.verticals?.length || 0;
          }
        }
      }

      return counter ? Math.round((viewedVerticals.length * 100) / counter) : 0;
    }
  }, [structure, viewedVerticals, loadedCourseDetail]);

  window.courseProgress = unitCount;

  const spentFromRows = (moduleInspects.data || []).reduce((sum, row) => {
    const detailSpent = Number(row?.details?.total_spent);
    if (!Number.isNaN(detailSpent) && detailSpent > 0) return sum + detailSpent;
    return sum;
  }, 0);
  const totalSpent = Number(moduleInspects.total_spent) > 0 ? Number(moduleInspects.total_spent) : spentFromRows;
  const totalHour = Math.floor((totalSpent || 0) / 3600);
  const totalMin = `0${Math.floor(((totalSpent || 0) % 3600) / 60)}`.slice(-2);

  return (
    <Box className="w-max flex justify-start items-center rounded-md border border-solid border-gray-200 dark:border-gray-500 dark:border-gray-500 bg-gray-50 dark:bg-gray-700 px-4 pl-2 py-3 gap-4">
      <Box className="flex items-center justify-center relative">
        <Box className="relative flex items-center" sx={{ transform: "scale(-1, 1)" }}>
          <CircularProgress variant="determinate" className="text-gray-200 w-12 h-12" size={40} thickness={4} value={100} />
          <CircularProgress variant="determinate" className="color-primary absolute left-0 w-12 h-12" sx={{ [`& .${circularProgressClasses.circle}`]: { strokeLinecap: "round" } }} size={40} thickness={4} value={unitCount} />
        </Box>
        <Box className="flex absolute w-full h-full top-0 left-0 items-center justify-center">
          <VText className="text-xs">{unitCount}%</VText>
        </Box>
      </Box>
      <Box>
        <VText className="text-sm" color="secondary" div>
          {t("Course Progresses")}
        </VText>
        <VText className="text-xl">
          {totalHour} {totalHour > 1 ? t("hours") : t("hour")} {totalMin} min
        </VText>
      </Box>
    </Box>
  );
};

export default BoardProgressBar;
